import { Assets, Container, Sprite } from 'pixi.js'
import { Spine } from '@esotericsoftware/spine-pixi-v8'
import { logger } from '../../framework/core/Logger'
import type { Orientation, Resizable } from '../../framework/render/design'
import { uiTexture } from '../assets/UiTextures'

// 場景 Spine 動畫層 — 沿用 Unity ani_bg（櫻花/瀑布/花瓣）與 ani_character（老虎）。
const BASE = import.meta.env.BASE_URL

export async function loadSceneSpines(): Promise<void> {
  Assets.add({ alias: 'sceneBgSkel', src: `${BASE}spine/bg/ani_bg.skel` })
  Assets.add({ alias: 'sceneBgAtlas', src: `${BASE}spine/bg/ani_bg.atlas` })
  Assets.add({ alias: 'sceneChSkel', src: `${BASE}spine/character/ani_character.skel` })
  Assets.add({ alias: 'sceneChAtlas', src: `${BASE}spine/character/ani_character.atlas` })
  await Assets.load(['sceneBgSkel', 'sceneBgAtlas', 'sceneChSkel', 'sceneChAtlas'])
}

export class SpineSceneView extends Container implements Resizable {
  private readonly bg: Spine
  private readonly character: Spine
  private readonly logo = new Sprite(uiTexture('logo'))

  constructor() {
    super()
    this.bg = Spine.from({ skeleton: 'sceneBgSkel', atlas: 'sceneBgAtlas' })
    this.character = Spine.from({ skeleton: 'sceneChSkel', atlas: 'sceneChAtlas' })
    this.playLoop(this.bg)
    this.playLoop(this.character)
    this.logo.anchor.set(0, 0)
    this.addChild(this.bg, this.character, this.logo)
  }

  resize(width: number, height: number, orientation: Orientation): void {
    // 背景：等比「覆蓋」全螢幕，以實際算繪邊界計算，並加少量 overscan 確保不留縫隙
    this.coverFullScreen(width, height)

    // 老虎角色：橫版置右側站岩石；直版縮小置於盤面下方右側留白
    const cd = this.character.skeleton.data
    if (cd.height > 0) {
      if (orientation === 'portrait') {
        // 直版：老虎探頭於框上方右側（對齊截圖2），與頂部中央 Bonus 轉盤、左上 Logo 並排
        this.character.scale.set((height * 0.2) / cd.height)
        this.character.position.set(width * 0.84, height * 0.13)
      } else {
        this.character.scale.set((height * 0.62) / cd.height)
        this.character.position.set(width * 0.85, height * 0.82)
      }
    }

    // Logo 左上（直版略大）
    const logoTex = this.logo.texture
    if (logoTex.width > 0) {
      const target = Math.min(width * (orientation === 'portrait' ? 0.34 : 0.2), logoTex.width)
      this.logo.scale.set(target / logoTex.width)
      this.logo.position.set(width * 0.015, height * 0.015)
    }
  }

  /** 等比覆蓋整個畫面（cover）；以實際局部邊界置中，overscan 防止邊緣縫隙。 */
  private coverFullScreen(width: number, height: number): void {
    const OVERSCAN = 1.04
    this.bg.scale.set(1)
    this.bg.position.set(0, 0)
    const bounds = this.bg.getLocalBounds()
    const bd = this.bg.skeleton.data
    const bw = bounds.width > 0 ? bounds.width : bd.width
    const bh = bounds.height > 0 ? bounds.height : bd.height
    if (bw <= 0 || bh <= 0) return
    const scale = Math.max(width / bw, height / bh) * OVERSCAN
    this.bg.scale.set(scale)
    // 將邊界中心對齊螢幕中心（getLocalBounds 為 Pixi 座標，Y 向下）
    const cx = bounds.x + bounds.width / 2
    const cy = bounds.y + bounds.height / 2
    this.bg.position.set(width / 2 - cx * scale, height / 2 - cy * scale)
  }

  private playLoop(spine: Spine): void {
    const animations = spine.skeleton.data.animations
    if (animations.length > 0) {
      spine.state.setAnimation(0, animations[0].name, true)
      logger.debug('Spine 動畫', animations.map((a) => a.name).join(', '))
    }
  }
}
